import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { User, LogOut, Loader2, Settings, Inbox, FlaskConical, Shield, ChevronDown, Check, LifeBuoy, Gem } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { SearchAutocomplete } from '../SearchAutocomplete';
import { MediaToggle } from '../MediaToggle';
import { AuthModal } from '../AuthModal';
import { NotificationBell } from '../NotificationBell';
import { InboxDropdown } from '../InboxDropdown';
import { useAuth, type TestProMode } from '../../contexts/AuthContext';
import { useMediaType } from '../../contexts/MediaTypeContext';
import { useInboxUnreadCount } from '../../hooks/useInboxQuery';
import { effectiveTier, type EntitlementTier } from '../../utils/entitlements';
import { queryKeys } from '../../lib/queryKeys';
import { isAdminUiAllowed } from '../../utils/adminAuth';
import { showIntercom, waitForIntercomBoot, ensureIntercomBoot } from '../../lib/intercom';
import { apiFetch } from '../../lib/api';
import { useToast } from '../ui/Toast';

interface TopBarProps {
  sidebarCollapsed?: boolean;
}

/**
 * InboxIcon - Inbox button with dropdown (orange when unread)
 *
 * Phase 4 v2 - Dropdown shows preview of inbox threads.
 * Similar pattern to NotificationBell.
 */
function InboxIcon() {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const { unreadCount } = useInboxUnreadCount();
  const hasUnread = unreadCount > 0;

  return (
    <>
      <button
        ref={anchorRef}
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-slate-400 hover:text-white transition-colors"
        aria-label={`Inbox${hasUnread ? ` (${unreadCount} unread)` : ''}`}
      >
        <Inbox
          size={20}
          className={hasUnread ? 'text-orange-400' : undefined}
        />
        {hasUnread && (
          <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 text-[10px] font-bold text-white bg-orange-500 rounded-full flex items-center justify-center">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {/* Inbox dropdown */}
      {isOpen && (
        <InboxDropdown
          anchorRef={anchorRef}
          onClose={() => setIsOpen(false)}
        />
      )}
    </>
  );
}

// Tier dropdown options for test users
const TIER_OPTIONS: { value: EntitlementTier; label: string; hint: string; color: string; textColor: string; ringColor: string; bgColor: string }[] = [
  { value: 'free', label: 'Free', hint: '10 chats, no social', color: 'bg-slate-500', textColor: 'text-slate-300', ringColor: 'ring-slate-500/30', bgColor: 'bg-slate-800/50' },
  { value: 'social', label: 'Social', hint: 'Social features, 10 chats', color: 'bg-teal-500', textColor: 'text-teal-300', ringColor: 'ring-teal-500/30', bgColor: 'bg-teal-500/10' },
  { value: 'pro', label: 'Pro', hint: 'Unlimited Filmply + social', color: 'bg-purple-500', textColor: 'text-purple-300', ringColor: 'ring-purple-500/30', bgColor: 'bg-purple-500/10' },
];

const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

type MoodStatusOption = {
  value: string;
  label: string;
  emoji: string;
  colorClass: string;
};

const MOOD_STATUS_OPTIONS: MoodStatusOption[] = [
  { value: 'cozy', label: 'Cozy', emoji: '🛋️', colorClass: 'bg-amber-400' },
  { value: 'adventurous', label: 'Adventurous', emoji: '🧭', colorClass: 'bg-emerald-400' },
  { value: 'drained', label: 'Drained', emoji: '😮‍💨', colorClass: 'bg-slate-400' },
  { value: 'romantic', label: 'Romantic', emoji: '💕', colorClass: 'bg-rose-400' },
  { value: 'need_a_laugh', label: 'Need a laugh', emoji: '😂', colorClass: 'bg-yellow-400' },
  { value: 'want_to_think', label: 'Want to think', emoji: '🧠', colorClass: 'bg-indigo-400' },
  { value: 'something_light', label: 'Something light', emoji: '☁️', colorClass: 'bg-cyan-400' },
  { value: 'mind_bending', label: 'Mind-bending', emoji: '🌀', colorClass: 'bg-purple-400' },
  { value: 'emotional_gut_punch', label: 'Emotional gut-punch', emoji: '💔', colorClass: 'bg-red-400' },
  { value: 'background_comfort', label: 'Background comfort', emoji: '🫶', colorClass: 'bg-teal-400' },
  { value: 'adrenaline_rush', label: 'Adrenaline rush', emoji: '⚡', colorClass: 'bg-orange-400' },
  { value: 'something_artsy', label: 'Something artsy', emoji: '🎨', colorClass: 'bg-fuchsia-400' },
];

function getStatusOption(options: MoodStatusOption[], value?: string | null): MoodStatusOption | null {
  if (!value) return null;
  return options.find((option) => option.value === value) ?? null;
}

/**
 * TestUserTierToggle - 3-option dropdown for test users only.
 *
 * Allows test users to preview gated experiences by selecting
 * Free, Social, or Pro tier without affecting real billing.
 * On toggle: updates DB, clears server-side For You cache,
 * purges frontend RQ cache, and refreshes profile.
 */
function TestUserTierToggle() {
  const { profile, updateProfile, refreshProfile, session } = useAuth();
  const queryClient = useQueryClient();
  const [isSaving, setIsSaving] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const lastToggleRef = useRef(0);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Only show for test users
  if (!profile?.is_test_user) {
    return null;
  }

  const currentTier = effectiveTier(profile);
  const currentOption = TIER_OPTIONS.find(o => o.value === currentTier) || TIER_OPTIONS[0];

  const handleSelectTier = async (newTier: EntitlementTier) => {
    if (isSaving || newTier === currentTier) {
      setIsOpen(false);
      return;
    }

    // 2s cooldown to prevent rapid toggles causing auth lock contention
    if (Date.now() - lastToggleRef.current < 2000) return;
    lastToggleRef.current = Date.now();

    const newMode: TestProMode = newTier;
    setIsOpen(false);
    setIsSaving(true);

    try {
      // 1. Update DB via profile update (awaited — must succeed before cache purge)
      const { error } = await updateProfile({ test_pro_mode: newMode });
      if (error) {
        console.error('Failed to toggle tier:', error);
        refreshProfile().catch((err) => console.warn('[TEST_TIER_TOGGLE] refreshProfile failed', err));
        return;
      }

      // 2. Clear server-side For You cache (fire-and-forget with 3s timeout)
      const accessToken = session?.access_token;
      if (accessToken) {
        (async () => {
          const controller = new AbortController();
          const timeoutId = setTimeout(() => controller.abort(), 3000);
          try {
            const resp = await fetch(`${API_BASE_URL}/test/clear-cache`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${accessToken}`,
                'Content-Type': 'application/json',
              },
              signal: controller.signal,
            });
            if (!resp.ok) {
              console.warn(`[TEST_TIER_TOGGLE] Server cache clear returned ${resp.status}`);
            }
          } catch (e) {
            console.warn('[TEST_TIER_TOGGLE] Server cache clear failed:', e);
          } finally {
            clearTimeout(timeoutId);
          }
        })();
      } else {
        console.warn('[TEST_TIER_TOGGLE] Cache clear skipped: no access token');
      }

      // 3. Purge frontend For You cache (remove, not invalidate — fully clear stale tier data)
      queryClient.removeQueries({
        queryKey: queryKeys.forYou.apiBase(profile.id),
      });

      // 4. Invalidate public profile page caches (tier badge may differ per tier)
      queryClient.invalidateQueries({ queryKey: ['profile'] });

      // 5. Refresh profile for deterministic auth/profile sync (fire-and-forget)
      refreshProfile().catch((err) => console.warn('[TEST_TIER_TOGGLE] refreshProfile failed', err));
    } catch (e) {
      console.error('Failed to toggle tier:', e);
      refreshProfile().catch((err) => console.warn('[TEST_TIER_TOGGLE] refreshProfile failed', err));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger button */}
      <button
        onClick={() => !isSaving && setIsOpen(!isOpen)}
        disabled={isSaving}
        className={`
          flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-medium
          transition-all duration-200 ring-1 ring-inset
          ${isSaving ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
          ${currentOption.bgColor} ${currentOption.textColor} ${currentOption.ringColor}
        `}
        title="Test User: Switch tier preview"
      >
        <FlaskConical size={14} />
        <span className="text-[10px] opacity-60">TEST</span>
        <span>{currentOption.label}</span>
        {isSaving ? (
          <Loader2 size={12} className="animate-spin" />
        ) : (
          <ChevronDown size={12} className={`transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        )}
      </button>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute top-full right-0 mt-1.5 w-52 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg shadow-xl shadow-black/40 z-50 overflow-hidden">
          {/* Header */}
          <div className="px-3 py-2 border-b border-white/[0.08]">
            <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">Test Tier</span>
          </div>

          {/* Options */}
          {TIER_OPTIONS.map((option) => {
            const isSelected = option.value === currentTier;
            return (
              <button
                key={option.value}
                onClick={() => handleSelectTier(option.value)}
                className={`
                  w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors
                  ${isSelected ? 'bg-white/[0.04]' : 'hover:bg-white/[0.04]'}
                `}
              >
                {/* Color dot */}
                <span className={`w-2 h-2 rounded-full shrink-0 ${option.color}`} />
                {/* Label + hint */}
                <div className="flex-1 min-w-0">
                  <div className={`text-xs font-medium ${isSelected ? option.textColor : 'text-slate-300'}`}>
                    {option.label}
                  </div>
                  <div className="text-[10px] text-slate-500 leading-tight">{option.hint}</div>
                </div>
                {/* Check mark */}
                {isSelected && <Check size={14} className={option.textColor} />}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function TopBar({ sidebarCollapsed = false }: TopBarProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user, profile, session, loading: authLoading, initialized, signOut, refreshProfile } = useAuth();
  const { toast } = useToast();
  const { mediaType } = useMediaType();

  // Show loading until auth is fully initialized
  const showAuthLoading = !initialized || authLoading;

  // Auth modal state
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState<'signin' | 'signup'>('signin');

  // User menu state
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);
  const userButtonRef = useRef<HTMLButtonElement>(null);
  const [menuPosition, setMenuPosition] = useState<{ top: number; right: number } | null>(null);

  // Check if we're on pages that show Movies/TV toggle
  const isDiscoverPage = location.pathname === '/';
  const isForYouPage = location.pathname === '/for-you';

  const computeMenuPosition = useCallback((rect: DOMRect) => ({
    top: rect.bottom + 8,
    right: window.innerWidth - rect.right,
  }), []);

  // Update menu position when showing
  const updateMenuPosition = useCallback(() => {
    if (!userButtonRef.current) return;
    const rect = userButtonRef.current.getBoundingClientRect();
    setMenuPosition(computeMenuPosition(rect));
  }, [computeMenuPosition]);

  // Get current search query from URL (for display in search box)
  const currentSearchQuery = isDiscoverPage ? (searchParams.get('q') || '') : '';

  const moodStatus = getStatusOption(MOOD_STATUS_OPTIONS, profile?.current_mood_status);
  const statusDotClass = moodStatus?.colorClass || 'bg-slate-500';
  const avatarStatusTitle = moodStatus
    ? `Current mood: ${moodStatus.emoji} ${moodStatus.label}`
    : 'No vibe status set';

  // Search handler - navigates to discover page with search query
  // Guard against no-op navigation to prevent URL/render ping-pong.
  const handleSearch = useCallback((rawQuery: string) => {
    const query = rawQuery.trim();

    // Empty query should only clear discover page search; do nothing elsewhere.
    if (!query && !isDiscoverPage) return;

    const params = new URLSearchParams(searchParams);
    if (query) {
      params.set('q', query);
    } else {
      params.delete('q');
    }

    const queryString = params.toString();
    const targetUrl = queryString ? `/?${queryString}` : '/';
    const currentUrl = `${location.pathname}${location.search}`;
    if (currentUrl === targetUrl) return;

    navigate(targetUrl, { replace: isDiscoverPage });
  }, [isDiscoverPage, location.pathname, location.search, navigate, searchParams]);

  // Handle menu position and close on outside click
  useEffect(() => {
    if (!showUserMenu) return;

    updateMenuPosition();
    window.addEventListener('scroll', updateMenuPosition, true);
    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('sidebar-collapse-change', updateMenuPosition);

    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node;
      if (
        userMenuRef.current && !userMenuRef.current.contains(target) &&
        userButtonRef.current && !userButtonRef.current.contains(target)
      ) {
        setShowUserMenu(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      window.removeEventListener('scroll', updateMenuPosition, true);
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('sidebar-collapse-change', updateMenuPosition);
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu, updateMenuPosition]);

  useEffect(() => {
    if (!showUserMenu) {
      setShowStatusMenu(false);
    }
  }, [showUserMenu]);

  const handleSetProfileStatus = useCallback(async (value: string | null) => {
    if (!user || !session?.access_token || isSavingStatus) return;
    setIsSavingStatus(true);
    try {
      await apiFetch('/user/profile', {
        method: 'PATCH',
        token: session.access_token,
        body: { current_mood_status: value },
      });
      // Refresh local profile state from server
      refreshProfile().catch(() => {});
    } catch (err) {
      console.error('[STATUS] Failed to update profile status:', err);
      toast.error('Failed to update your status. Please try again.');
    } finally {
      setIsSavingStatus(false);
    }
  }, [isSavingStatus, user, session, refreshProfile, toast]);

  return (
    <>
      <header
        className={`fixed top-0 right-0 z-30 h-16 bg-[#0F1419]/95 backdrop-blur-sm border-b border-white/[0.06] transition-all duration-200 ${
          sidebarCollapsed ? 'left-[72px]' : 'left-[260px]'
        }`}
      >
        <div className="h-full px-4 flex items-center justify-between gap-4">
          {/* Left side - Movies/TV toggle (discover + for-you pages) */}
          <div className="flex items-center gap-4">
            {(isDiscoverPage || isForYouPage) && <MediaToggle />}
          </div>

          {/* Center - Search Bar */}
          <div className="flex-1 max-w-xl mx-auto">
            <SearchAutocomplete
              mediaType={mediaType}
              onSearch={handleSearch}
              searchQuery={currentSearchQuery}
            />
          </div>

          {/* Right side - Test toggle + Inbox + Notification bell + User menu */}
          <div className="flex items-center gap-3">
            {/* Test user tier toggle (only visible to test users) */}
            {user && <TestUserTierToggle />}

            {/* Inbox icon */}
            {user && <InboxIcon />}

            {/* Notification bell */}
            {user && <NotificationBell />}

            {/* Auth Buttons / User Menu */}
            {showAuthLoading ? (
              /* Loading state - show skeleton */
              <div className="flex items-center gap-2 px-3 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg">
                <div className="w-7 h-7 bg-slate-700 rounded-full flex items-center justify-center animate-pulse">
                  <Loader2 size={14} className="text-slate-500 animate-spin" />
                </div>
                <div className="hidden sm:block w-16 h-4 bg-slate-700 rounded animate-pulse" />
              </div>
            ) : user ? (
              /* Logged in - User Menu */
              <div className="relative">
                <button
                  ref={userButtonRef}
                  onClick={() => {
                    if (showUserMenu) {
                      setShowUserMenu(false);
                      return;
                    }
                    if (userButtonRef.current) {
                      const rect = userButtonRef.current.getBoundingClientRect();
                      setMenuPosition(computeMenuPosition(rect));
                    }
                    setShowUserMenu(true);
                  }}
                  className="flex items-center gap-2 px-3 py-2 bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg hover:ring-white/[0.12] transition-colors"
                  title={avatarStatusTitle}
                >
                  <div className="w-7 h-7 bg-gradient-to-br from-teal-400 to-orange-500 rounded-full flex items-center justify-center overflow-hidden relative">
                    {profile?.avatar_url ? (
                      <>
                        {/* Always show fallback behind image */}
                        <User size={16} className="text-white absolute" />
                        <img
                          src={profile.avatar_url}
                          alt={profile.username || 'Avatar'}
                          className="w-full h-full object-cover relative z-10"
                          loading="eager"
                        />
                      </>
                    ) : (
                      <User size={16} className="text-white" />
                    )}
                    <span className={`absolute -right-0.5 -bottom-0.5 h-2.5 w-2.5 rounded-full ring-2 ring-[#1A1F26] ${statusDotClass}`} />
                  </div>
                  <span className="hidden sm:block text-sm text-slate-300 max-w-[120px] truncate">
                    {profile?.username || user.email?.split('@')[0]}
                  </span>
                </button>

                {/* Dropdown Menu - portaled to body for proper z-index stacking */}
                {showUserMenu && menuPosition && createPortal(
                  <div
                    ref={userMenuRef}
                    className="fixed w-72 max-w-[calc(100vw-2rem)] bg-[#1A1F26] ring-1 ring-white/[0.08] rounded-lg shadow-xl shadow-black/40 z-40"
                    style={{ top: menuPosition.top, right: menuPosition.right }}
                  >
                    <div className="p-3 border-b border-white/[0.08]">
                      {profile?.username && (
                        <p className="text-sm font-medium text-white truncate">@{profile.username}</p>
                      )}
                      <p className="text-xs text-slate-400 truncate">{user.email}</p>
                    </div>
                    {profile?.username && (
                      <Link
                        to={`/u/${profile.username}`}
                        onClick={() => setShowUserMenu(false)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors"
                      >
                        <User size={16} />
                        View Profile
                      </Link>
                    )}
                    <div className="px-3 pb-2">
                      <button
                        type="button"
                        onClick={() => setShowStatusMenu((prev) => !prev)}
                        className="mt-1 w-full flex items-center justify-between gap-2 rounded-lg px-2 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors"
                      >
                        <span className="flex items-center gap-2">
                          <span className={`h-2 w-2 rounded-full ${statusDotClass}`} />
                          Set vibe status
                        </span>
                        <ChevronDown
                          size={14}
                          className={`transition-transform ${showStatusMenu ? 'rotate-180' : ''}`}
                        />
                      </button>
                      {showStatusMenu && (
                        <div className="mt-2 space-y-3 rounded-lg bg-[#0F1419] p-2 ring-1 ring-white/[0.06]">
                          <div>
                            <p className="mb-1 px-1 text-[11px] font-medium text-slate-400">How are you feeling today?</p>
                            <div className="flex flex-wrap gap-1.5">
                              {MOOD_STATUS_OPTIONS.map((option) => {
                                const isActive = profile?.current_mood_status === option.value;
                                return (
                                  <button
                                    key={option.value}
                                    type="button"
                                    disabled={isSavingStatus}
                                    onClick={() => handleSetProfileStatus(isActive ? null : option.value)}
                                    className={`rounded-full px-2 py-1 text-[11px] transition-colors ${
                                      isActive
                                        ? 'bg-teal-500/20 text-teal-300 ring-1 ring-teal-500/40'
                                        : 'bg-white/5 text-slate-300 hover:bg-white/10'
                                    } ${isSavingStatus ? 'opacity-60 cursor-not-allowed' : ''}`}
                                  >
                                    {option.emoji} {option.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                    {/* Admin link - only shown if user is allowed */}
                    {isAdminUiAllowed(user) && (
                      <Link
                        to="/admin"
                        onClick={() => setShowUserMenu(false)}
                        className="w-full flex items-center gap-2 px-3 py-2 text-sm text-orange-400 hover:bg-slate-700 transition-colors"
                      >
                        <Shield size={16} />
                        Admin
                      </Link>
                    )}
                    <Link
                      to="/settings"
                      onClick={() => setShowUserMenu(false)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors"
                    >
                      <Settings size={16} />
                      Settings
                    </Link>
                    <Link
                      to="/pricing"
                      onClick={() => setShowUserMenu(false)}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors"
                    >
                      <Gem size={16} />
                      Pricing
                    </Link>
                    {/* Support chat — Intercom with mailto fallback */}
                    <button
                      id="filmple-support-launcher"
                      onClick={async () => {
                        // Close menu immediately — user must not see it hanging open during wait
                        setShowUserMenu(false);

                        // Try immediate open if already booted
                        if (showIntercom()) return;

                        // Actively boot Intercom if we have credentials
                        if (user?.id && session?.access_token) {
                          try {
                            const data = await apiFetch<{ token: string; expires_in: number }>(
                              '/user/intercom-jwt',
                              { token: session.access_token }
                            );
                            await ensureIntercomBoot(data.token, user.id, user.email);
                            if (showIntercom()) return;
                          } catch {
                            // Boot attempt failed — fall through to wait/fallback
                          }
                        }

                        // Last chance — wait for background boot to finish
                        const ready = await waitForIntercomBoot(8000, 100);
                        if (ready && showIntercom()) return;

                        // Fallback — Intercom not available
                        window.location.href = 'mailto:support@filmple.io';
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors"
                    >
                      <LifeBuoy size={16} />
                      Chat to Support
                    </button>
                    <div className="my-1 border-t border-white/[0.08]" />
                    <Link
                      to="/terms"
                      onClick={() => setShowUserMenu(false)}
                      className="w-full block px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-700 hover:text-slate-300 transition-colors"
                    >
                      Terms and Conditions
                    </Link>
                    <Link
                      to="/privacy-policy"
                      onClick={() => setShowUserMenu(false)}
                      className="w-full block px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-700 hover:text-slate-300 transition-colors"
                    >
                      Privacy Policy
                    </Link>
                    <Link
                      to="/cookie-policy"
                      onClick={() => setShowUserMenu(false)}
                      className="w-full block px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-700 hover:text-slate-300 transition-colors"
                    >
                      Cookie Policy
                    </Link>
                    <Link
                      to="/disclaimer"
                      onClick={() => setShowUserMenu(false)}
                      className="w-full block px-3 py-1.5 text-xs text-slate-500 hover:bg-slate-700 hover:text-slate-300 transition-colors"
                    >
                      Disclaimer
                    </Link>
                    <div className="my-1 border-t border-white/[0.08]" />
                    <button
                      onClick={() => {
                        signOut();
                        setShowUserMenu(false);
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 text-sm text-slate-300 hover:bg-slate-700 transition-colors"
                    >
                      <LogOut size={16} />
                      Sign Out
                    </button>
                  </div>,
                  document.body
                )}
              </div>
            ) : (
              /* Not logged in - Sign In / Sign Up buttons */
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    setAuthMode('signin');
                    setShowAuthModal(true);
                  }}
                  className="px-4 py-2 text-sm font-medium text-slate-300 hover:text-white transition-colors"
                >
                  Sign In
                </button>
                <button
                  onClick={() => {
                    setAuthMode('signup');
                    setShowAuthModal(true);
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-teal-500 to-teal-400 rounded-lg hover:from-teal-400 hover:to-teal-300 transition-all"
                >
                  Sign Up
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Auth Modal */}
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        initialMode={authMode}
      />
    </>
  );
}
